import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/Avatar';
import { bestPhotoFor } from '@/lib/photo';
import { toast } from 'sonner';
import {
  Mail, Phone, IndianRupee, Briefcase, Building2, CalendarDays,
  CalendarCheck, Star, FileText, Award, TrendingUp, ShieldCheck, Camera, Loader2, Trash2, Plus, Printer, Eye, Download
} from 'lucide-react';
import { DocumentComposer } from '@/components/documents/DocumentComposer';
import { LetterheadShell, printLetterhead } from '@/components/documents/LetterheadShell';
import { downloadDocument } from '@/lib/downloadDocument';
// Phase 246 — Canonical DD/MM/YYYY for the certificates ledger + review timestamps.
import { formatGlobalDate } from '@/utils/dateFormatter';

// Phase 88 — Employee 360 detail modal. Mirrors the Student Analytics modal in
// spirit: header with avatar + identity badges, three tabbed views (Profile,
// Attendance, Reviews). Lazy-loads attendance/reviews on first tab activation
// to keep the open animation snappy.
//
// Props: { emp, open, onClose }

export function EmployeeDetailModal({ emp, open, onClose, onEmployeeUpdated }) {
  const { user } = useAuth();
  const isOwner = user?.role === 'academy_owner' || user?.role === 'super_admin';
  const [tab, setTab] = useState('profile');
  const [attendance, setAttendance] = useState(null);
  const [reviews, setReviews] = useState(null);
  // Phase 121 — inline document locker state
  const [documents, setDocuments] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  // Phase 88.1 — Inline profile-photo upload
  const [photoOverride, setPhotoOverride] = useState(null); // hot-swap shown after upload, before parent refetch
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Reset state when the modal opens for a different employee
  useEffect(() => {
    if (open) { setTab('profile'); setAttendance(null); setReviews(null); setPhotoOverride(null); setDocuments(null); }
  }, [open, emp?.id]);

  useEffect(() => {
    if (!emp?.id || !open) return;
    if (tab === 'attendance' && attendance === null) {
      api.get(`/employee-attendance?employee_id=${emp.id}`)
        .then(r => setAttendance(r.data || []))
        .catch(() => setAttendance([]));
    }
    if (tab === 'reviews' && reviews === null) {
      api.get(`/performance-reviews?employee_id=${emp.id}`)
        .then(r => setReviews(r.data || []))
        .catch(() => setReviews([]));
    }
    // Phase 121 — lazy-load docs ledger on first tab activation
    if (tab === 'documents' && documents === null) {
      api.get('/documents', { params: { target_type: 'employee', target_id: emp.id } })
        .then(r => setDocuments(Array.isArray(r.data) ? r.data : []))
        .catch(() => setDocuments([]));
    }
  }, [tab, emp?.id, open, attendance, reviews, documents]);

  const refreshDocuments = async () => {
    try {
      const r = await api.get('/documents', { params: { target_type: 'employee', target_id: emp.id } });
      setDocuments(Array.isArray(r.data) ? r.data : []);
    } catch (_e) { /* silent */ }
  };

  if (!emp) return null;

  const photoSrc = photoOverride !== null ? photoOverride : bestPhotoFor(emp);

  // Phase 88.1 — Owner uploads a profile photo for this employee.
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error('Max 4 MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/employees/${emp.id}/profile-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoOverride(r.data.photo_url);
      toast.success('Photo updated');
      onEmployeeUpdated?.({ ...emp, profile_photo_url: r.data.photo_url, profile_photo_path: r.data.storage_path });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePhotoRemove = async () => {
    if (!window.confirm('Remove this employee\'s profile photo?')) return;
    setUploading(true);
    try {
      await api.delete(`/employees/${emp.id}/profile-photo`);
      setPhotoOverride('');
      toast.success('Photo removed');
      onEmployeeUpdated?.({ ...emp, profile_photo_url: null, profile_photo_path: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="employee-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative shrink-0">
                <Avatar src={photoSrc} name={emp.name} size={48} tone="staff" ring="ring-2 ring-indigo-100 dark:ring-indigo-900" testId="emp-modal-avatar" />
                {isOwner && (
                  <>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center shadow-md border-2 border-white dark:border-slate-900 transition-colors disabled:opacity-50"
                      title="Upload new photo"
                      data-testid="emp-photo-upload-btn"
                    >
                      {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      className="hidden"
                      onChange={handlePhotoChange}
                      data-testid="emp-photo-upload-input"
                    />
                  </>
                )}
              </div>
              <div className="min-w-0">
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <span className="truncate" data-testid="emp-modal-name">{emp.name}</span>
                  <span className="text-xs font-mono text-muted-foreground">{emp.custom_id}</span>
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge variant="outline" className="text-xs">{emp.role}</Badge>
                  {emp.department && <Badge variant="secondary" className="text-xs">{emp.department}</Badge>}
                  {emp.status && <Badge className={`text-xs ${emp.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'} border-0`}>{emp.status}</Badge>}
                  {isOwner && photoSrc && (
                    <button onClick={handlePhotoRemove} disabled={uploading} className="text-[10px] text-muted-foreground hover:text-red-600 inline-flex items-center gap-0.5 disabled:opacity-50" data-testid="emp-photo-remove-btn">
                      <Trash2 className="h-2.5 w-2.5" /> Remove photo
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Base Salary</p>
              <p className="text-lg font-display font-bold tabular-nums" data-testid="emp-modal-salary">₹{(emp.base_salary || 0).toLocaleString()}</p>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="w-full">
            <TabsTrigger value="profile" className="flex-1" data-testid="emp-tab-profile"><Briefcase className="h-3.5 w-3.5 mr-1" /> Profile</TabsTrigger>
            <TabsTrigger value="documents" className="flex-1" data-testid="emp-tab-documents"><FileText className="h-3.5 w-3.5 mr-1" /> Documents</TabsTrigger>
            <TabsTrigger value="attendance" className="flex-1" data-testid="emp-tab-attendance"><CalendarCheck className="h-3.5 w-3.5 mr-1" /> Attendance</TabsTrigger>
            <TabsTrigger value="reviews" className="flex-1" data-testid="emp-tab-reviews"><Star className="h-3.5 w-3.5 mr-1" /> Reviews</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="flex-1 overflow-auto space-y-3 mt-3" data-testid="emp-profile-panel">
            <Card>
              <CardContent className="p-4 grid sm:grid-cols-2 gap-3 text-sm">
                <Row icon={<Mail className="h-3.5 w-3.5" />} label="Email" value={emp.email || '—'} testId="emp-profile-email" />
                <Row icon={<Phone className="h-3.5 w-3.5" />} label="Phone" value={emp.phone || '—'} testId="emp-profile-phone" />
                <Row icon={<Briefcase className="h-3.5 w-3.5" />} label="Designation" value={emp.designation || emp.role || '—'} />
                <Row icon={<Building2 className="h-3.5 w-3.5" />} label="Department" value={emp.department || '—'} />
                <Row icon={<IndianRupee className="h-3.5 w-3.5" />} label="Base Salary" value={`₹${(emp.base_salary || 0).toLocaleString()}/mo`} />
                <Row icon={<CalendarDays className="h-3.5 w-3.5" />} label="Joined" value={emp.join_date || '—'} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Phase 121 — Compliance Document Locker (live) */}
          <TabsContent value="documents" className="flex-1 overflow-auto space-y-3 mt-3" data-testid="emp-documents-panel">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3 no-print">
                  <div>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-indigo-600" />
                      <p className="text-sm font-semibold">Compliance Document Locker</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Offer letters, salary slips, experience certificates — every letter ever issued lives here, immutable.
                    </p>
                  </div>
                  {isOwner && (
                    <Button
                      size="sm"
                      onClick={() => setComposerOpen(true)}
                      data-testid="emp-doc-compose-btn"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8 px-3"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" /> Compose Letter
                    </Button>
                  )}
                </div>

                {documents === null ? (
                  <Skeleton className="h-32 w-full rounded-lg" />
                ) : documents.length === 0 ? (
                  <div className="py-8 text-center text-xs text-muted-foreground border border-dashed rounded-lg"
                       data-testid="emp-doc-empty">
                    No documents issued for {emp.name || 'this employee'} yet.
                    {isOwner && <> Click <strong>Compose Letter</strong> to issue the first one.</>}
                  </div>
                ) : (
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm" data-testid="emp-doc-table">
                      <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">Ref</th>
                          <th className="px-3 py-2 text-left font-semibold">Subject</th>
                          <th className="px-3 py-2 text-left font-semibold hidden sm:table-cell">Date</th>
                          <th className="px-3 py-2 text-right font-semibold no-print">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {documents.map((d) => (
                          <tr key={d.id} className="hover:bg-muted/20 transition-colors"
                              data-testid={`emp-doc-row-${d.id}`}>
                            <td className="px-3 py-2.5 font-mono text-[11px] text-muted-foreground">
                              {d.reference_no || d.id.slice(0, 8)}
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="font-semibold text-foreground text-xs leading-tight">{d.subject || '—'}</div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">{d.template_name || d.template_slug || ''}</div>
                            </td>
                            <td className="px-3 py-2.5 text-[11px] text-muted-foreground hidden sm:table-cell">
                              {formatGlobalDate(d.issued_date || d.created_at) || '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right no-print">
                              <div className="flex justify-end gap-1.5">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-[11px] px-2"
                                  onClick={() => setPreviewDoc(d)}
                                  data-testid={`emp-doc-view-${d.id}`}
                                >
                                  <Eye className="h-3 w-3 mr-1" /> View
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-7 text-[11px] px-2 bg-slate-900 hover:bg-slate-800 text-white"
                                  onClick={() => { setPreviewDoc(d); setTimeout(printLetterhead, 220); }}
                                  data-testid={`emp-doc-print-${d.id}`}
                                >
                                  <Printer className="h-3 w-3 mr-1" /> Print
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="attendance" className="flex-1 overflow-auto space-y-3 mt-3" data-testid="emp-attendance-panel">
            {attendance === null ? (
              <Skeleton className="h-40 w-full rounded-lg" />
            ) : (
              <AttendancePanel records={attendance} />
            )}
          </TabsContent>

          <TabsContent value="reviews" className="flex-1 overflow-auto space-y-3 mt-3" data-testid="emp-reviews-panel">
            {reviews === null ? (
              <Skeleton className="h-40 w-full rounded-lg" />
            ) : (
              <ReviewsPanel reviews={reviews} />
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* Phase 121 — DocumentComposer (issue new letters) */}
      <DocumentComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        targetType="employee"
        target={emp}
        academy={null}
        onSaved={() => { setComposerOpen(false); refreshDocuments(); }}
      />

      {/* Phase 121 — Re-print preview via LetterheadShell (Phase 120 print CSS already inline) */}
      {previewDoc && (
        <Dialog open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="emp-doc-preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between no-print">
                <span className="flex items-center gap-2"><FileText className="h-4 w-4" /> {previewDoc.subject || 'Document'}</span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={printLetterhead}
                    data-testid="emp-doc-preview-print"
                    className="h-8 text-xs"
                    title="Uses your browser print dialog. Backgrounds and logos may render differently across devices."
                  >
                    <Printer className="h-3.5 w-3.5 mr-1" /> Print Now
                  </Button>
                  <div className="flex flex-col items-end">
                    <Button
                      size="sm"
                      onClick={() => downloadDocument(api, `/documents/${previewDoc.id}/pdf`, `${previewDoc.custom_id || previewDoc.id || 'document'}.pdf`, { label: previewDoc.subject })}
                      data-testid="emp-doc-preview-download"
                      className="bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs"
                    >
                      <Download className="h-3.5 w-3.5 mr-1" /> Download PDF
                    </Button>
                    <span className="text-[10px] text-slate-400 mt-0.5 select-none">Recommended for best fidelity</span>
                  </div>
                </div>
              </DialogTitle>
            </DialogHeader>
            <LetterheadShell
              academy={previewDoc.academy_snapshot || null}
              subject={previewDoc.subject}
              addressedTo={previewDoc.addressed_to}
              dateLabel={previewDoc.issued_date || ''}
              bodyText={previewDoc.body_text}
              signingAuthority={previewDoc.signing_authority}
              documentId={previewDoc.reference_no || previewDoc.id}
            />
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

function Row({ icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-2 min-w-0" data-testid={testId}>
      <span className="text-muted-foreground mt-0.5">{icon}</span>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</p>
        <p className="text-sm font-medium truncate">{value}</p>
      </div>
    </div>
  );
}

function AttendancePanel({ records }) {
  const total = records.length;
  const present = records.filter(r => r.status === 'present').length;
  const absent = records.filter(r => r.status === 'absent').length;
  const leave = records.filter(r => r.status === 'leave').length;
  const rate = total > 0 ? Math.round((present / total) * 100) : 0;

  return (
    <>
      <div className="grid grid-cols-4 gap-2">
        <StatTile label="Present" value={present} accent="text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30" />
        <StatTile label="Absent" value={absent} accent="text-red-600 bg-red-50 dark:bg-red-950/30" />
        <StatTile label="Leave" value={leave} accent="text-amber-600 bg-amber-50 dark:bg-amber-950/30" />
        <StatTile label="Rate" value={`${rate}%`} accent="text-primary bg-primary/5" />
      </div>
      <Card>
        <CardContent className="p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Recent log</p>
          {records.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No attendance records yet.</p>
          ) : (
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {records.slice(0, 60).map((r, i) => (
                <div key={r.id || i} className="flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-muted/50" data-testid={`emp-attendance-row-${i}`}>
                  <span className="tabular-nums">{r.date}</span>
                  <Badge className={`text-[10px] border-0 ${
                    r.status === 'present' ? 'bg-emerald-100 text-emerald-700' :
                    r.status === 'absent' ? 'bg-red-100 text-red-700' :
                    r.status === 'leave' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>{r.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function ReviewsPanel({ reviews }) {
  if (reviews.length === 0) {
    return (
      <div className="text-center py-10" data-testid="emp-reviews-empty">
        <Star className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
        <p className="text-sm font-medium">No performance reviews yet</p>
        <p className="text-xs text-muted-foreground mt-1">Reviews appear here once the academy owner files one.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {reviews.map((r, i) => (
        <Card key={r.id} data-testid={`emp-review-${i}`}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-amber-500" />
                <p className="text-sm font-semibold">Q{r.quarter} {r.year}</p>
              </div>
              <Badge className="bg-amber-100 text-amber-700 border-0 text-xs tabular-nums">{r.overall_score?.toFixed(1) ?? '—'}/10</Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mb-2">
              <div className="flex items-center gap-1.5"><TrendingUp className="h-3 w-3 text-emerald-600" /> Player growth: <span className="font-semibold tabular-nums">{r.player_growth_score}/10</span></div>
              <div className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-blue-600" /> Consistency: <span className="font-semibold tabular-nums">{r.training_consistency_score}/10</span></div>
            </div>
            {r.strengths && <p className="text-xs"><span className="font-semibold">Strengths:</span> {r.strengths}</p>}
            {r.improvement_areas && <p className="text-xs mt-1"><span className="font-semibold">Improvement:</span> {r.improvement_areas}</p>}
            {r.notes && <p className="text-xs mt-1 text-muted-foreground italic">"{r.notes}"</p>}
            <p className="text-[10px] text-muted-foreground mt-1.5">By {r.reviewer_name || 'Reviewer'} · {formatGlobalDate(r.created_at || r.updated_at)}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div className={`p-2 rounded-lg text-center ${accent}`}>
      <p className="text-sm font-bold tabular-nums">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

export default EmployeeDetailModal;
